/**
 * Cache promise ber-TTL + dedup in-flight — helper MURNI (tanpa Supabase/DOM),
 * lahir untuk PF-02 (audit 2026-08): `fetchSiteSettings` dipanggil 2–5× per
 * halaman oleh 44+ call-site hook tanpa cache apa pun. Pola cermin promise-cache
 * `getCurrentAdminProfile` (admin-management.ts), digenerikkan supaya bisa
 * diuji dan dipakai ulang.
 *
 * Kontrak:
 *  • `get(key, loader)` — pemanggilan seKEY selama TTL berbagi SATU hasil;
 *    pemanggilan saat loader masih terbang ikut menumpang promise yang sama
 *    (dedup in-flight).
 *  • KEGAGALAN TIDAK di-cache: loader yang reject dilepas dari cache sehingga
 *    pemanggil berikutnya mencoba lagi — jangan sampai satu error jaringan
 *    membekukan halaman selama TTL.
 *  • `invalidate(key)` / `invalidate()` — buang satu entri / semua entri
 *    (dipakai saat admin menyimpan setelan supaya perubahan langsung terlihat).
 */
export type TtlPromiseCache<T> = {
  get(key: string, loader: () => Promise<T>): Promise<T>;
  invalidate(key?: string): void;
};

export function createTtlPromiseCache<T>(
  ttlMs: number,
  now: () => number = Date.now
): TtlPromiseCache<T> {
  const entries = new Map<string, { at: number; promise: Promise<T> }>();

  return {
    get(key, loader) {
      const hit = entries.get(key);
      if (hit && now() - hit.at < ttlMs) return hit.promise;

      const promise = loader();
      const entry = { at: now(), promise };
      entries.set(key, entry);
      promise.catch(() => {
        // Jangan cache kegagalan — tapi hanya bila entri ini masih yang
        // terpasang (invalidate/refresh lain bisa sudah menggantikannya).
        if (entries.get(key) === entry) entries.delete(key);
      });
      return promise;
    },
    invalidate(key) {
      if (key === undefined) entries.clear();
      else entries.delete(key);
    },
  };
}
